// src/components/Category/Category.jsx
// Main component for Category Management page with modern design
// Loads all data at once and paginates client-side

import React, { useState, useEffect } from 'react';
import categoriesService from '../../services/categoryService';
import CategoryTable from './CategoryTable';
import CategoryModal from './CategoryModal';
import EditCategoryModal from './EditCategoryModal';
import { showNotification } from '../../utils/notification';
import ConfirmDialog from "../common/ConfirmDialog";
import { PlusCircle } from 'lucide-react';
import './category.css';

/**
 * Category Component
 * Loads all categories once and handles filtering/pagination client-side
 */
const Category = ({ toggleSidebar }) => {
  // ========== STATE MANAGEMENT ==========
  const [allCategories, setAllCategories] = useState([]); // All categories from backend
  const [filteredCategories, setFilteredCategories] = useState([]); // After applying filters
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'active' | 'inactive'
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Delete confirmation modal
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ========== LOAD ALL DATA ONCE ==========
  useEffect(() => {
    loadAllCategories();
  }, []);

  // ========== APPLY FILTERS WHEN SEARCH CHANGES ==========
  useEffect(() => {
    applyFilters();
  }, [searchTerm, statusFilter, allCategories]);

  /**
   * Fetch ALL categories from the backend (no pagination)
   */
  const loadAllCategories = async () => {
    try {
      setIsLoading(true);
      // Load all data at once (use a large limit or separate endpoint)
      const data = await categoriesService.getAll(1, 1000); // Get up to 1000 categories
      setAllCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
      showNotification(`Failed to load categories: ${error.message}`, 'error');
      setAllCategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Apply client-side filtering
   */
  const applyFilters = () => {
    let filtered = [...allCategories];

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(cat => {
        const name = cat.name?.toLowerCase() || '';
        return name.includes(searchLower);
      });
    }

    // Filter by status
    if (statusFilter) {
      if (statusFilter === 'active') {
        filtered = filtered.filter(cat => cat.isActive === true);
      } else if (statusFilter === 'inactive') {
        filtered = filtered.filter(cat => cat.isActive === false);
      }
    }

    setFilteredCategories(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  /**
   * Get paginated data for current page
   */
  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredCategories.slice(startIndex, endIndex);
  };

  /**
   * Calculate total pages
   */
  const getTotalPages = () => {
    return Math.ceil(filteredCategories.length / itemsPerPage);
  };

  /**
   * Handle search input change
   */
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  /**
   * Go to specific page
   */
  const goToPage = (page) => {
    const totalPages = getTotalPages();
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  /**
   * Handle page navigation
   */
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = getTotalPages();
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // ========== MODAL OPERATIONS ==========
  const handleAddNew = () => {
    setSelectedCategory(null);
    setIsModalOpen(true);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedCategory(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedCategory(null);
  };

  // ========== CRUD OPERATIONS ==========
  const handleSave = async (payload) => {
    try {
      setIsSaving(true);

      if (selectedCategory) {
        payload.categories[0].id = selectedCategory.id;
        await categoriesService.update(selectedCategory.id, payload);
        showNotification(
          `Category "${payload.name}" updated successfully!`,
          'success'
        );
      } else {
        await categoriesService.create(payload);
        showNotification(
          `New category "${payload.name}" added successfully!`,
          'success'
        );
      }

      await loadAllCategories(); // Reload all data
      handleCloseModal();

    } catch (error) {
      console.error('Error saving category:', error);
      showNotification(`Failed to save: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // ========== DELETE CONFIRMATION ==========
  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setIsConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      setIsDeleting(true);
      await categoriesService.delete(categoryToDelete.id);
      showNotification("Category deleted successfully", "success");
      await loadAllCategories(); // Reload all data
    } catch (error) {
      console.error("Delete failed:", error);
      showNotification(`Failed to delete: ${error.message}`, "error");
    } finally {
      setIsDeleting(false);
      setIsConfirmOpen(false);
      setCategoryToDelete(null);
    }
  };

  const cancelDelete = () => {
    setIsConfirmOpen(false);
    setCategoryToDelete(null);
  };

  // ========== UPDATE CATEGORY ==========
  const handleSaveEdits = async (formData) => {
    try {
      setIsSavingEdit(true);

      const payload = {
        name: formData.name,
        isActive: formData.isActive,
      };

      await categoriesService.update(selectedCategory.id, payload);

      showNotification(
        `Category "${payload.name}" updated successfully!`,
        'success'
      );

      await loadAllCategories(); // Reload all data
      handleCloseEditModal();

    } catch (error) {
      console.error('Edit failed:', error);
      showNotification(`Failed to update: ${error.message}`, 'error');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Get current page data
  const paginatedCategories = getPaginatedData();
  const totalPages = getTotalPages();
  const startItem = filteredCategories.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, filteredCategories.length);

  // ========== RENDER ==========
  return (
    <main className="inventory-main-content">
      {/* Page Header */}
      <header className="inventory-content-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={toggleSidebar} className="sidebar-toggle-btn">
            ☰
          </button>
          <h2 className="inventory-content-title">Category Management</h2>
        </div>
        <div className="header-actions">
          <button 
            className="inventory-btn-add" 
            onClick={handleAddNew}
          >
            <PlusCircle size={18} />
            Add Category
          </button>
        </div>
      </header>

      {/* Filter Controls */}
      <section className="inventory-filter-controls">
        <div className="inventory-filter-group">
          {/* Search Label and Input */}
          <div>
            <label className="filter-label">Search Categories</label>
            <div style={{ position: 'relative' }}>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#9CA3AF" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none'
                }}
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                name="search"
                className="inventory-custom-input inventory-search-input"
                placeholder="Search by category name..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ paddingLeft: '40px' }}
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="filter-label">Status Filter</label>
            <select
              className="inventory-custom-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          {/* Clear Filters Button */}
          <button 
            className="inventory-btn inventory-btn-clear" 
            onClick={handleClearFilters}
            style={{ marginTop: '24px' }}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              style={{ marginRight: '6px' }}
            >
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
              <line x1="12" y1="3" x2="12" y2="3.01"></line>
            </svg>
            Clear Filters
          </button>
        </div>
      </section>

      {/* Data Table */}
      <CategoryTable
        categories={paginatedCategories}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        isLoading={isLoading}
      />

      {/* Pagination Info */}
      {!isLoading && filteredCategories.length > 0 && (
        <div style={{
          textAlign: 'center',
          fontSize: '14px',
          color: '#6B7280',
          fontWeight: '500',
          marginTop: '15px',
          padding: '10px',
          background: 'white',
          borderRadius: '8px'
        }}>
          Showing {startItem} to {endItem} of {filteredCategories.length} entries
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button 
            className="btn-page" 
            disabled={currentPage === 1} 
            onClick={handlePreviousPage}
          >
            Previous
          </button>
          
          <div className="page-numbers">
            {currentPage > 2 && (
              <>
                <button className="btn-page-num" onClick={() => goToPage(1)}>1</button>
                {currentPage > 3 && <span className="page-ellipsis">...</span>}
              </>
            )}
            
            {currentPage > 1 && (
              <button 
                className="btn-page-num" 
                onClick={() => goToPage(currentPage - 1)}
              >
                {currentPage - 1}
              </button>
            )}
            
            <button className="btn-page-num active">{currentPage}</button>
            
            {currentPage < totalPages && (
              <button 
                className="btn-page-num" 
                onClick={() => goToPage(currentPage + 1)}
              >
                {currentPage + 1}
              </button>
            )}
            
            {currentPage < totalPages - 1 && (
              <>
                {currentPage < totalPages - 2 && <span className="page-ellipsis">...</span>}
                <button className="btn-page-num" onClick={() => goToPage(totalPages)}>{totalPages}</button>
              </>
            )}
          </div>

          <span className="page-info">Page {currentPage} of {totalPages || 1}</span>
          
          <button 
            className="btn-page" 
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={handleNextPage}
          >
            Next
          </button>
        </div>
      )}

      {/* Add Modal */}
      <CategoryModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        category={selectedCategory}
        isSaving={isSaving}
      />

      {/* Edit Modal */}
      <EditCategoryModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleSaveEdits}
        category={selectedCategory}
        isSaving={isSavingEdit}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Delete Category"
        message={`Are you sure you want to delete "${categoryToDelete?.name}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
      />
    </main>
  );
};

export default Category;